export function validateHeadshot(file, maxMegabytes = 10) {
  if (!file || !file.size) return 'Please choose a photo that is not empty.';
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return 'Please choose a JPG, PNG, or WebP photo.';
  if (file.size > maxMegabytes * 1024 * 1024) return `Please choose a photo smaller than ${maxMegabytes} MB.`;
  return '';
}

export function headshotUrl(result) {
  const value = result && typeof result.url === 'string' ? result.url : '';
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.username || url.password) throw new Error('The photo service did not return a secure photo link.');
  return url.href;
}

if (typeof window !== 'undefined' && !customElements.get('ls-client-feedback')) {
  customElements.define('ls-client-feedback', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = this.querySelector('form');
      this.fileInput = this.querySelector('[data-headshot-input]');
      this.photoUrlInput = this.querySelector('[data-headshot-url]');
      this.photoConsentInput = this.querySelector('[data-headshot-consent]');
      this.preview = this.querySelector('[data-headshot-preview]');
      this.filename = this.querySelector('[data-headshot-filename]');
      this.fileError = this.querySelector('[data-headshot-error]');
      this.removeButton = this.querySelector('[data-headshot-remove]');
      this.submitButton = this.querySelector('[type="submit"]');
      if (!this.form || !this.fileInput) return;
      this.maxMegabytes = Number(this.dataset.maxMegabytes) || 10;
      this.endpoint = '';
      try { if (this.dataset.uploadEndpoint) this.endpoint = headshotUrl({ url: this.dataset.uploadEndpoint }); } catch { /* Unavailable uploads stay disabled. */ }
      if (this.endpoint) {
        this.fileInput.disabled = false;
        this.querySelector('[data-upload-unavailable]')?.setAttribute('hidden', '');
        this.querySelector('[data-upload-label]')?.removeAttribute('aria-disabled');
      }
      this.fileInput.addEventListener('change', () => this.selectPhoto(), options);
      this.removeButton?.addEventListener('click', () => this.clearPhoto(), options);
      this.form.addEventListener('submit', event => this.submit(event), options);
      this.form.addEventListener('change', event => {
        if (event.target.matches('[data-publication-preference]')) this.photoConsentInput.value = event.target.value;
      }, options);
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
      this.initialized = false;
    }

    clearPhoto() {
      this.fileInput.value = '';
      this.photoUrlInput.value = '';
      this.uploaded = false;
      if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = '';
      this.preview.hidden = true;
      this.preview.removeAttribute('src');
      this.filename.textContent = '';
      this.removeButton.hidden = true;
      this.fileError.textContent = '';
      this.fileError.hidden = true;
      this.fileInput.removeAttribute('aria-invalid');
    }

    selectPhoto() {
      const file = this.fileInput.files[0];
      this.photoUrlInput.value = '';
      this.uploaded = false;
      if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = '';
      this.preview.hidden = true;
      this.preview.removeAttribute('src');
      this.filename.textContent = '';
      this.removeButton.hidden = !file;
      this.fileError.textContent = '';
      this.fileError.hidden = true;
      this.fileInput.removeAttribute('aria-invalid');
      if (!file) return;
      const error = validateHeadshot(file, this.maxMegabytes);
      if (error) {
        this.fileInput.setAttribute('aria-invalid', 'true');
        this.fileError.textContent = error;
        this.fileError.hidden = false;
        this.filename.textContent = file.name;
        return;
      }
      this.filename.textContent = file.name;
      this.previewObjectUrl = URL.createObjectURL(file);
      this.preview.src = this.previewObjectUrl;
      this.preview.hidden = false;
    }

    async submit(event) {
      if (this.busy) { event.preventDefault(); return; }
      const file = this.fileInput.files[0];
      if (!file || this.uploaded) return;
      event.preventDefault();
      const error = validateHeadshot(file, this.maxMegabytes);
      if (error || !this.endpoint) {
        this.fileError.textContent = error || 'Photo uploads are unavailable. Remove the photo to send your written feedback.';
        this.fileError.hidden = false;
        this.fileInput.focus();
        return;
      }
      const selectedPermission = this.form.querySelector('[data-publication-preference]:checked');
      if (!selectedPermission || !this.form.reportValidity()) return;
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      this.submitButton.disabled = true;
      this.fileInput.disabled = true;
      const buttonText = this.submitButton.querySelector('[data-submit-text]');
      const originalText = buttonText.textContent;
      buttonText.textContent = 'Uploading photo…';
      this.removeButton.disabled = true;
      const data = new FormData();
      data.append('file', file);
      // Storage is provisional. Only the final contact submission records consent.
      data.append('publication_status', 'pending_final_submission');
      data.append('full_name', this.form.querySelector('[data-full-name]').value);
      const timeoutController = new AbortController();
      const timer = setTimeout(() => timeoutController.abort(), 45000);
      try {
        const response = await fetch(this.endpoint, { method: 'POST', body: data, signal: timeoutController.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Photo upload failed. Please try again, or remove the photo to send your feedback.');
        const result = await response.json();
        this.photoUrlInput.value = headshotUrl(result);
        const currentPermission = this.form.querySelector('[data-publication-preference]:checked');
        this.photoConsentInput.value = currentPermission ? currentPermission.value : '';
        this.uploaded = true;
        this.busy = false;
        this.submitButton.disabled = false;
        this.fileInput.disabled = false;
        buttonText.textContent = originalText;
        this.form.removeAttribute('aria-busy');
        this.form.requestSubmit(this.submitButton);
      } catch (uploadError) {
        this.fileError.textContent = uploadError.name === 'AbortError' ? 'Photo upload timed out. Please try again, or remove the photo to send your feedback.' : uploadError.message;
        this.fileError.hidden = false;
        this.fileError.focus();
      } finally {
        clearTimeout(timer);
        this.busy = false;
        this.submitButton.disabled = false;
        this.removeButton.disabled = false;
        this.fileInput.disabled = false;
        buttonText.textContent = originalText;
        this.form.removeAttribute('aria-busy');
      }
    }
  });
}
